import React from 'react';
import { Box, Checkbox, IconButton, Stack, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ResponsiveTable, { ResponsiveTableColumn } from '../common/ResponsiveTable';
import type { CostType, DocMode, QuotationLine } from '../../types/e2i';
import { costTypeName, formatAmount, formatPct, formatQuantity } from '../../utils/e2i';

interface Props {
  lines: QuotationLine[];
  costTypes: CostType[];
  currency: string;
  docMode: DocMode;
  editable: boolean;
  disabled?: boolean;
  selected: number[];
  onToggle: (index: number) => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
}

type Row = { line: QuotationLine; index: number };

/** The priced lines (what the client sees and SAP gets), with the cost each comes from. */
const QuotationLinesTable: React.FC<Props> = ({ lines, costTypes, currency, docMode, editable, disabled, selected, onToggle, onEdit, onDelete }) => {
  const rows: Row[] = lines.map((line, index) => ({ line, index }));
  const columns: ResponsiveTableColumn<Row>[] = [
    ...(editable ? [{
      id: 'select',
      header: <Box component="span" className="visually-hidden">Select to merge</Box>,
      width: 44,
      cell: (r: Row) => (
        <Checkbox
          size="small"
          checked={selected.includes(r.index)}
          onChange={() => onToggle(r.index)}
          disabled={disabled}
          inputProps={{ 'aria-label': `Select line ${r.index + 1} to merge` }}
        />
      ),
    }] : []),
    { id: 'n', header: '#', cell: (r) => r.index + 1, width: 40 },
    {
      id: 'desc',
      header: 'Description',
      cell: (r) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2">{r.line.description}</Typography>
          <Typography variant="caption" color="text.secondary">
            {r.line.costType ? `${costTypeName(r.line.costType, costTypes)} · markup ${formatPct(r.line.markupPct)}` : 'Fixed price'}
          </Typography>
        </Box>
      ),
    },
    { id: 'qty', header: 'Quantity', align: 'right', cell: (r) => `${formatQuantity(r.line.quantity)}${r.line.uom ? ` ${r.line.uom}` : ''}` },
    { id: 'cost', header: `Unit cost ${currency}`.trim(), align: 'right', hideOnMobile: true, cell: (r) => (r.line.unitCost ? formatAmount(r.line.unitCost) : '—') },
    { id: 'price', header: `Unit price ${currency}`.trim(), align: 'right', cell: (r) => formatAmount(r.line.unitPrice) },
    { id: 'amount', header: `Amount ${currency}`.trim(), align: 'right', cell: (r) => <Typography variant="body2" fontWeight={600}>{formatAmount(r.line.amount)}</Typography> },
    {
      id: 'target',
      header: docMode === 'Service' ? 'Revenue account' : 'Sales item',
      hideOnMobile: true,
      cell: (r) => (docMode === 'Service' ? r.line.account : r.line.salesItem) || <Typography variant="caption" color="text.secondary">Default</Typography>,
    },
    ...(editable ? [{
      id: 'actions',
      header: '',
      align: 'right' as const,
      cell: (r: Row) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <IconButton size="small" aria-label={`Edit line ${r.index + 1}`} onClick={() => onEdit(r.index)} disabled={disabled}><EditOutlinedIcon fontSize="small" /></IconButton>
          <IconButton size="small" aria-label={`Remove line ${r.index + 1}`} onClick={() => onDelete(r.index)} disabled={disabled}><DeleteOutlineIcon fontSize="small" /></IconButton>
        </Stack>
      ),
    }] : []),
  ];
  return (
    <ResponsiveTable
      ariaLabel="Quotation lines"
      columns={columns}
      rows={rows}
      getRowKey={(r) => r.index}
      emptyMessage={editable ? 'No lines: add a priced line, or prepare the quotation again from the estimate' : 'No lines'}
    />
  );
};

export default QuotationLinesTable;
